/* eslint-disable max-lines -- Pre-existing violation */
import {
  Button,
  ButtonGroup,
  Card,
  DialogFooter,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Separator,
  showNotification,
} from 'platform/components';
import {Box, Grid, Heading, HStack, Show, Space, Stack, VStack} from 'platform/foundation';
import * as Yup from 'yup';

import {UseFormReturn} from 'react-hook-form';

import {
  always,
  any,
  defaultTo,
  head,
  ifElse,
  isNil,
  isNotNil,
  omit,
  pipe,
  reject,
  values,
} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {AddrDetailApiResponse} from '@dms/api/core';
import {
  AddressRequestBodyV2,
  CommonAddressRequestBody,
  CustomerResponseBodyV2,
  EmailData,
  PhoneNumberData,
  useLazyGetCustomerV2Query,
  usePatchCustomerV2Mutation,
} from '@dms/api/customer';
import {PhoneNumber} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {ApiError, Nullish, suffixTestId, TestIdProps, yupString} from 'shared/web';

import {emptyIdentityCard, emptyPersonData} from '../../../constants/emptyValues';
import {useCustomerGroups} from '../../../hooks/useCustomerGroups';
import {useGender} from '../../../hooks/useGender';
import {useParsePersonalId} from '../../../hooks/useParsePersonalId';
import {useTenant} from '../../../hooks/useTenant';
import {getEmailsRequestBody} from '../../../utils/getEmailsRequestBody';
import {handleApiError} from '../../../utils/handleApiError';
import {isPhoneNumberValid} from '../../../utils/isPhoneNumberValid';
import {isPolishTenant} from '../../../utils/isPolishTenant';
import {
  FORM_ROOT_ERROR_NAME,
  mapCustomerValidationErrors,
} from '../../../utils/mapCustomerValidationErrors';
import {yupCustomerEmail} from '../../../utils/yupCustomerEmail';
import {AddressSearch} from '../../AddressSearch/AddressSearch';
import {CountrySelect} from '../../CountrySelect/CountrySelect';
import {DecodeBusinessIdButton} from '../../DecodeBusinessIdButton/DecodeBusinessIdButton';
import {EmailList} from '../../EmailList/EmailList';
import {FormRootErrorAlert} from '../../FormRootErrorAlert/FormRootErrorAlert';
import {IdentityCardList} from '../../IdentityCardList/IdentityCardList';
import {yupIdentityCardSchema} from '../../IdentityCardList/yupIdentityCardSchema';
import {PhoneNumberList} from '../../PhoneNumberList/PhoneNumberList';
import {useCreateCustomerWithAddress} from '../hooks/useCreateCustomerWithAddress';
import {CreateCustomerProps} from '../types/CreateCustomerProps';
import {PhysicalPersonFormType} from '../types/PhysicalPersonFormType';
import {handleDecodeBusinessIdForm} from '../utils/handleDecodeBusinessIdForm';

interface NewPhysicalPersonFormProps extends TestIdProps {
  customer: CreateCustomerProps;
  onCreate: (customer: CustomerResponseBodyV2) => void;
  onClose: () => void;
}

// eslint-disable-next-line complexity, max-lines-per-function -- Pre-existing violation
export function PhysicalPersonForm(props: NewPhysicalPersonFormProps) {
  const {customerGroupOptions} = useCustomerGroups();
  const {genderOptions} = useGender();
  const parsePersonalId = useParsePersonalId();

  const [getCustomer, {isLoading: isGetCustomerLoading}] = useLazyGetCustomerV2Query();
  const {createCustomerWithAddress, isLoading: isCreateCustomerLoading} =
    useCreateCustomerWithAddress();
  const [patchCustomer, {isLoading: isPatchCustomerLoading}] = usePatchCustomerV2Mutation();

  const onSubmit: FormSubmitHandler<PhysicalPersonFormType> = async (data, setErrors) => {
    const {emails, formIndexes} = getEmailsRequestBody(data.foundingPerson?.emails);
    const phoneNumbers = reject(
      (entry) => isNilOrEmpty(entry.phoneNumber?.number),
      data.foundingPerson?.phoneNumbers ?? []
    );

    const createdCustomer = await createCustomerWithAddress({
      createCustomerRequestBody: {
        customerDiscriminator: data.customerData.customerDiscriminator,
        email: head(emails)?.email ?? null,
        firstName: data.foundingPerson?.personData.firstName ?? null,
        lastName: data.foundingPerson?.personData.lastName ?? null,
        phoneNumber: head(phoneNumbers)?.phoneNumber ?? null,
        registrationNumber: null,
        tradeName: null,
        customerGroups: data.customerGroups ?? [],
      },
      locale: data.locale,
      addressData: data.addressData,
    }).catch((error: ApiError) => {
      handleApiError(error, {
        silentOnValidationError: true,
        setErrors: (errors) =>
          setErrors(
            errors?.map(({name, message}) =>
              name === 'email'
                ? {name: `foundingPerson.emails.${head(formIndexes) ?? 0}.email`, message}
                : {
                    name: FORM_ROOT_ERROR_NAME,
                    message: i18n.t('entity.customer.validations.invalidCustomerData'),
                  }
            ) ?? null
          ),
      });
      return null;
    });

    if (isNil(createdCustomer)) {
      return;
    }

    const {customerId, addressId} = createdCustomer;
    const foundingPerson = isNil(data.foundingPerson)
      ? null
      : {
          ...data.foundingPerson,
          phoneNumbers,
          emails,
          identityCards: reject(
            (entry) => isNilOrEmpty(entry.cardData?.cardNumber),
            data.foundingPerson.identityCards ?? []
          ),
          permanentAddressId: addressId ?? data.foundingPerson.permanentAddressId,
        };

    await patchCustomer({
      customerId,
      customerRequestBody: {
        ...omit(['addressData'], data),
        foundingPerson,
        selfEmployedBusinessInfoData: data.selfEmployed ? data.selfEmployedBusinessInfoData : null,
        selfEmployedBusinessAddressId: data.selfEmployed
          ? (addressId ?? data.selfEmployedBusinessAddressId)
          : data.selfEmployedBusinessAddressId,
      },
    })
      .unwrap()
      .then(() => getCustomer({customerId}).unwrap())
      .then(props.onCreate)
      .catch((error: ApiError) =>
        handleApiError(error, {
          silentOnValidationError: true,
          setErrors: (errors) =>
            setErrors(
              mapCustomerValidationErrors(errors, {
                emailsPath: 'foundingPerson.emails',
                emailFormIndexes: formIndexes,
              })
            ),
        })
      );
  };

  const {tenantPhoneInfo, tenantCountry, supportedLocales, isMultiLocale, tenant} = useTenant();
  const isPolish = isPolishTenant(tenantCountry);

  const setAddressFormSearch =
    (formApi: UseFormReturn<PhysicalPersonFormType>) => (address: AddrDetailApiResponse) => {
      if (isNil(tenantCountry)) {
        showNotification.error();
        return;
      }

      formApi.setValue(
        'addressData.address',
        {
          city: address?.city ?? '',
          street: address?.street ?? '',
          zip: address?.zipCode ?? '',
          state: address?.state ?? '',
          prefix: address?.prefix ?? '',
          descriptiveNumber: address?.descriptiveNumber ?? null,
          orientationNumber: address?.orientationNumber ?? null,
          addressComplement: null,
          district: null,
          country: tenantCountry,
          coordinates: null,
          postal: null,
        },
        {shouldValidate: true}
      );
    };

  const defaultValues: PhysicalPersonFormType = {
    selfEmployed: props.customer.isSelfEmployed,
    locale: tenant?.defaultLocale ?? 'en-GB',
    selfEmployedBusinessInfoData: {
      registrationNumber: props.customer.registrationNumber ?? null,
      countryOfRegistrationCode: null,
      vatNumber: props.customer.vatNumber ?? null,
      tradeName: null,
      fileNumber: null,
    },
    customerData: {
      customerDiscriminator: props.customer.customerDiscriminator,
      customerSource: null,
    },
    customerGroups: props.customer.customerGroups,
    foundingPerson: {
      personData: {
        ...emptyPersonData,
        firstName: props.customer.firstName ?? null,
        lastName: props.customer.lastName ?? null,
      },
      identityCards: [emptyIdentityCard],
      permanentAddressId: null,
      phoneNumbers: [
        {
          phoneNumber: isNotNilOrEmpty(props.customer.phoneNumber?.number)
            ? props.customer.phoneNumber!
            : {
                prefix: tenantPhoneInfo.prefix,
                number: '',
                countryCode: tenantPhoneInfo.countryCode,
              },
          type: null,
        },
      ],
      emails: [
        {
          email: props.customer.email ?? '',
          type: null,
        },
      ],
    },
    businessInfo: null,
    addressData: {
      address: {
        city: props.customer.address?.city ?? '',
        street: props.customer.address?.street ?? '',
        zip: props.customer.address?.zip ?? '',
        state: props.customer.address?.state ?? '',
        prefix: props.customer.address?.prefix ?? '',
        descriptiveNumber: props.customer.address?.descriptiveNumber ?? '',
        orientationNumber: props.customer.address?.orientationNumber ?? '',
        addressComplement: props.customer.address?.addressComplement ?? '',
        district: props.customer.address?.district ?? '',
        country: props.customer.address?.country ?? '',
        coordinates:
          props.customer.address?.coordinates &&
          isNotNil(props.customer.address.coordinates.longitude) &&
          isNotNil(props.customer.address.coordinates.latitude)
            ? {
                longitude: String(props.customer.address.coordinates.longitude),
                latitude: String(props.customer.address.coordinates.latitude),
              }
            : null,
        postal: props.customer.address?.postal || null,
      },
      type: null,
      invalid: null,
    },
  };

  const isLoading = isCreateCustomerLoading || isPatchCustomerLoading || isGetCustomerLoading;

  return (
    <Form<PhysicalPersonFormType>
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      schema={getSchema(isMultiLocale)}
    >
      {/* eslint-disable-next-line max-lines-per-function -- Pre-existing violation */}
      {(control, formApi) => (
        <>
          <VStack spacing={4}>
            <FormRootErrorAlert control={control} data-testid={suffixTestId('form', props)} />
            <Stack direction={['column', 'row']} spacing={4}>
              <Box flex={1}>
                <FormField
                  control={control}
                  type="text"
                  name="foundingPerson.personData.titleBefore"
                  maxLength={10}
                  label={i18n.t('entity.person.labels.titleBefore')}
                  data-testid={suffixTestId('titleBefore', props)}
                />
              </Box>
              <Box flex={2}>
                <FormField
                  control={control}
                  type="text"
                  name="foundingPerson.personData.firstName"
                  isRequired
                  maxLength={64}
                  label={i18n.t('entity.person.labels.firstName')}
                  data-testid={suffixTestId('firstName', props)}
                />
              </Box>
              <Box flex={2}>
                <FormField
                  control={control}
                  type="text"
                  name="foundingPerson.personData.lastName"
                  isRequired
                  maxLength={64}
                  label={i18n.t('entity.person.labels.lastName')}
                  data-testid={suffixTestId('lastName', props)}
                />
              </Box>
              <Box flex={1}>
                <FormField
                  control={control}
                  type="text"
                  name="foundingPerson.personData.titleAfter"
                  maxLength={10}
                  label={i18n.t('entity.person.labels.titleAfter')}
                  data-testid={suffixTestId('titleAfter', props)}
                />
              </Box>
            </Stack>
            <Card variant="inlineGrey">
              <VStack spacing={4}>
                <Box width="fit-content">
                  <FormField
                    control={control}
                    type="switch"
                    name="selfEmployed"
                    label={i18n.t('entity.customer.labels.isSelfEmployed')}
                    data-testid={suffixTestId('registrationNumber', props)}
                  />
                </Box>
                <Show when={formApi.watch('selfEmployed')}>
                  <Box data-testid={suffixTestId('selfEmployedWrapper', props)}>
                    <VStack spacing={4}>
                      <Stack direction={['column', 'row']} spacing={4}>
                        <Box flex={1}>
                          <FormField
                            control={control}
                            type="text"
                            name="selfEmployedBusinessInfoData.registrationNumber"
                            label={i18n.t('entity.businessInfo.labels.registrationNumber')}
                            data-testid={suffixTestId('registrationNumber', props)}
                          />
                        </Box>
                        <Box flex={1}>
                          <FormField
                            control={control}
                            type="text"
                            name="selfEmployedBusinessInfoData.vatNumber"
                            label={i18n.t('entity.businessInfo.labels.vatNumber')}
                            data-testid={suffixTestId('vatNumber', props)}
                          />
                        </Box>
                        <HStack height={[undefined, 13]} align="flex-end">
                          <DecodeBusinessIdButton
                            businessId={formApi.watch(
                              'selfEmployedBusinessInfoData.registrationNumber'
                            )}
                            vatId={formApi.watch('selfEmployedBusinessInfoData.vatNumber')}
                            countryCode={formApi.watch(
                              'selfEmployedBusinessInfoData.countryOfRegistrationCode'
                            )}
                            onSuccess={handleDecodeBusinessIdForm(formApi, isPolish)}
                            data-testid={suffixTestId('decode', props)}
                          />
                        </HStack>
                      </Stack>
                      <Box flex={1}>
                        <FormField
                          control={control}
                          type="text"
                          name="selfEmployedBusinessInfoData.tradeName"
                          label={i18n.t('entity.businessInfo.labels.tradeName')}
                          isRequired
                          data-testid={suffixTestId('tradeName', props)}
                        />
                      </Box>
                      <Stack direction={['column', 'row']} spacing={4}>
                        <Box flex={1}>
                          <FormField
                            control={control}
                            type="text"
                            name="selfEmployedBusinessInfoData.fileNumber"
                            label={i18n.t('entity.businessInfo.labels.fileNumber')}
                            data-testid={suffixTestId('fileNumber', props)}
                          />
                        </Box>
                        <Box flex={1}>
                          <CountrySelect<PhysicalPersonFormType>
                            control={control}
                            name="selfEmployedBusinessInfoData.countryOfRegistrationCode"
                            label={i18n.t('entity.businessInfo.labels.countryOfRegistrationCode')}
                            isRequired
                            menuInPortal
                            data-testid={suffixTestId('countryOfRegistrationCode', props)}
                          />
                        </Box>
                      </Stack>
                    </VStack>
                  </Box>
                </Show>
              </VStack>
            </Card>
            <Stack direction={['column', 'row']} spacing={4}>
              <Box flex={1}>
                <CountrySelect<PhysicalPersonFormType>
                  control={control}
                  name="foundingPerson.personData.citizenshipCode"
                  label={i18n.t('entity.person.labels.citizenshipCode')}
                  onChange={() => parsePersonalId(formApi)}
                  menuInPortal
                  data-testid={suffixTestId('citizenshipCode', props)}
                />
              </Box>
              <Box flex={1}>
                <FormField
                  control={control}
                  type="text"
                  name="foundingPerson.personData.personalIdentifier"
                  label={i18n.t('entity.person.labels.personalIdentifier')}
                  onChange={() => parsePersonalId(formApi)}
                  data-testid={suffixTestId('personalIdentifier', props)}
                />
              </Box>
            </Stack>
            <Stack direction={['column', 'row']} spacing={4}>
              <Box flex={1}>
                <FormField
                  control={control}
                  type="choice"
                  name="foundingPerson.personData.genderKey"
                  options={genderOptions}
                  label={i18n.t('entity.person.labels.genderKey')}
                  menuInPortal
                  data-testid={suffixTestId('genderKey', props)}
                />
              </Box>
              <Box flex={1}>
                <FormField
                  control={control}
                  type="apiDate"
                  name="foundingPerson.personData.birthdate"
                  isRelativeDatesHidden
                  label={i18n.t('entity.person.labels.birthdate')}
                  data-testid={suffixTestId('birthdate', props)}
                />
              </Box>
            </Stack>
            <Heading size={4}>{i18n.t('entity.person.labels.contactInformation')}</Heading>
            <PhoneNumberList<PhysicalPersonFormType>
              control={control}
              formApi={formApi}
              name="foundingPerson.phoneNumbers"
              data-testid={suffixTestId('phoneNumberList', props)}
            />
            <EmailList<PhysicalPersonFormType>
              control={control}
              formApi={formApi}
              name="foundingPerson.emails"
              data-testid={suffixTestId('emailList', props)}
            />
            <Show when={isMultiLocale}>
              <Separator spacing={0} />
              <Heading size={4}>{i18n.t('general.labels.locales')}</Heading>
              <Grid columns={2}>
                <FormField
                  name="locale"
                  label={i18n.t('entity.person.labels.locale')}
                  type="choice"
                  options={supportedLocales}
                  control={control}
                />
              </Grid>
            </Show>
            <Separator spacing={0} />
            <Heading size={4}>{i18n.t('entity.person.permanentAddressId')}</Heading>
            <AddressSearch onSelect={setAddressFormSearch(formApi)} />
            <Show when={!isPolish}>
              <Stack direction={['column', 'row']} spacing={4}>
                <Box flex={1}>
                  <FormField
                    isRequired
                    control={control}
                    name="addressData.address.street"
                    type="text"
                    label={i18n.t('entity.address.labels.street')}
                    data-testid={suffixTestId('street', props)}
                  />
                </Box>
                <Box flex={1}>
                  <HStack spacing={4}>
                    <Box flex={1}>
                      <FormField
                        control={control}
                        name="addressData.address.descriptiveNumber"
                        type="text"
                        label={i18n.t('entity.address.labels.descriptiveNumber')}
                        data-testid={suffixTestId('descriptiveNumber', props)}
                      />
                    </Box>
                    <Box flex={1}>
                      <FormField
                        control={control}
                        name="addressData.address.orientationNumber"
                        type="text"
                        label={i18n.t('entity.address.labels.orientationNumber')}
                        data-testid={suffixTestId('orientationNumber', props)}
                      />
                    </Box>
                  </HStack>
                </Box>
              </Stack>
              <Stack direction={['column', 'row']} spacing={4}>
                <Box flex={1}>
                  <FormField
                    isRequired
                    control={control}
                    name="addressData.address.city"
                    type="text"
                    label={i18n.t('entity.address.labels.city')}
                    data-testid={suffixTestId('city', props)}
                  />
                </Box>
                <Box flex={1}>
                  <HStack spacing={4}>
                    <Box flex={1}>
                      <FormField
                        control={control}
                        name="addressData.address.district"
                        type="text"
                        label={i18n.t('entity.address.labels.district')}
                        data-testid={suffixTestId('postalCode', props)}
                      />
                    </Box>
                    <Box flex={1}>
                      <FormField
                        control={control}
                        name="addressData.address.zip"
                        type="text"
                        label={i18n.t('entity.address.labels.zipCode')}
                        data-testid={suffixTestId('zipCode', props)}
                        isRequired
                      />
                    </Box>
                  </HStack>
                </Box>
              </Stack>
              <Stack direction={['column', 'row']} spacing={4}>
                <Box flex={1}>
                  <CountrySelect<PhysicalPersonFormType>
                    isRequired
                    control={control}
                    name="addressData.address.country"
                    label={i18n.t('entity.address.labels.country')}
                    menuInPortal
                    data-testid={suffixTestId('country', props)}
                  />
                </Box>
                <Box flex={1}>
                  <FormField
                    control={control}
                    name="addressData.address.addressComplement"
                    type="text"
                    label={i18n.t('entity.address.labels.addressComplement')}
                    data-testid={suffixTestId('addressComplement', props)}
                  />
                </Box>
              </Stack>
              <FormField
                control={control}
                name="addressData.type"
                type="text"
                label={i18n.t('entity.address.labels.type')}
                data-testid={suffixTestId('type', props)}
              />
              <Space fillAvailable />
            </Show>
            <Show when={isPolish}>
              <Stack direction={['column', 'row']} spacing={4}>
                <Box flex={1}>
                  <FormField
                    isRequired
                    control={control}
                    name="addressData.address.street"
                    type="text"
                    label={i18n.t('entity.address.labels.street')}
                    data-testid={suffixTestId('street', props)}
                  />
                </Box>
                <Box flex={1}>
                  <HStack spacing={4}>
                    <Box flex={1}>
                      <FormField
                        control={control}
                        name="addressData.address.descriptiveNumber"
                        type="text"
                        label={i18n.t('entity.address.labels.descriptiveNumber')}
                        data-testid={suffixTestId('descriptiveNumber', props)}
                      />
                    </Box>
                    <Box flex={1}>
                      <FormField
                        control={control}
                        name="addressData.address.orientationNumber"
                        type="text"
                        label={i18n.t('entity.address.labels.orientationNumber')}
                        data-testid={suffixTestId('orientationNumber', props)}
                      />
                    </Box>
                  </HStack>
                </Box>
              </Stack>
              <Stack direction={['column', 'row']} spacing={4}>
                <Box flex={1}>
                  <FormField
                    isRequired
                    control={control}
                    name="addressData.address.city"
                    type="text"
                    label={i18n.t('entity.address.labels.city')}
                    data-testid={suffixTestId('city', props)}
                  />
                </Box>
                <Box flex={1}>
                  <HStack spacing={4}>
                    <Box flex={1}>
                      <FormField
                        control={control}
                        name="addressData.address.district"
                        type="text"
                        label={i18n.t('entity.address.labels.district')}
                        data-testid={suffixTestId('postalCode', props)}
                      />
                    </Box>
                    <Box flex={1}>
                      <FormField
                        isRequired
                        control={control}
                        name="addressData.address.zip"
                        type="text"
                        label={i18n.t('entity.address.labels.zipCode')}
                        data-testid={suffixTestId('zipCode', props)}
                      />
                    </Box>
                  </HStack>
                </Box>
              </Stack>
              <Stack direction={['column', 'row']} spacing={4}>
                <Box flex={1}>
                  <CountrySelect<PhysicalPersonFormType>
                    isRequired
                    control={control}
                    name="addressData.address.country"
                    label={i18n.t('entity.address.labels.country')}
                    menuInPortal
                    data-testid={suffixTestId('country', props)}
                  />
                </Box>
                <Box flex={1}>
                  <FormField
                    control={control}
                    name="addressData.address.addressComplement"
                    type="text"
                    label={i18n.t('entity.address.labels.addressComplement')}
                    data-testid={suffixTestId('addressComplement', props)}
                  />
                </Box>
              </Stack>
              <FormField
                control={control}
                name="addressData.type"
                type="text"
                label={i18n.t('entity.address.labels.type')}
                data-testid={suffixTestId('type', props)}
              />
              <Space fillAvailable />
            </Show>
            <Separator spacing={0} />
            <Heading size={4}>{i18n.t('general.labels.identityCard')}</Heading>
            <IdentityCardList<PhysicalPersonFormType>
              name="foundingPerson.identityCards"
              control={control}
              formApi={formApi}
              data-testid={suffixTestId('identityCardList', props)}
            />
            <Separator spacing={0} />
            <Heading size={4}>{i18n.t('entity.person.segmentation')}</Heading>
            <Grid columns={2}>
              <FormField
                name="customerGroups"
                label={i18n.t('entity.person.labels.customerGroups')}
                type="multiChoice"
                options={customerGroupOptions}
                menuPlacement="top"
                menuInPortal
                isNotClearable
                control={control}
                data-testid={suffixTestId('customerGroups', props)}
              />
            </Grid>
          </VStack>
          <DialogFooter>
            <ButtonGroup align="right">
              <Button
                variant="secondary"
                onClick={props.onClose}
                isDisabled={isLoading}
                title={i18n.t('general.actions.discard')}
                data-testid={suffixTestId('discard', props)}
              />
              <FormButton
                variant="primary"
                type="submit"
                control={control}
                isLoading={isLoading}
                title={i18n.t('general.actions.create')}
                data-testid={suffixTestId('create', props)}
              />
            </ButtonGroup>
          </DialogFooter>
        </>
      )}
    </Form>
  );
}

const getSchema = (isMultiLocale: boolean) =>
  Yup.object().shape(
    {
      locale: isMultiLocale ? yupString.required() : yupString.optional().nullable(),
      selfEmployed: Yup.boolean(),
      customerGroups: Yup.array(yupString),
      foundingPerson: Yup.object().shape(
        {
          personData: Yup.object().shape({
            titleBefore: yupString.optional(),
            firstName: yupString.required().max(64),
            lastName: yupString.required().max(64),
            titleAfter: yupString.optional(),
            genderKey: yupString.optional(),
            citizenshipCode: yupString.optional(),
            personalIdentifier: yupString.optional(),
          }),
          identityCards: Yup.array().of(yupIdentityCardSchema),
          phoneNumbers: Yup.array().when('emails', {
            is: (emails?: EmailData[]) =>
              emails?.length && emails.length <= 1 && isNilOrEmpty(emails?.[0]?.email),
            then: (schema) =>
              schema
                .of(
                  Yup.object().shape({
                    type: yupString.optional(),
                    phoneNumber: Yup.mixed()
                      .test(
                        'isPhone',
                        i18n.t('general.validations.invalidPhoneNumber'),
                        (phone?: PhoneNumber | Nullish) => {
                          if (isNilOrEmpty(phone?.number)) {
                            return true;
                          }
                          return isPhoneNumberValid(phone);
                        }
                      )
                      .test(
                        'required',
                        i18n.t('general.validations.phoneOrEmail'),
                        (phone?: PhoneNumber | Nullish) => isNotNilOrEmpty(phone?.number)
                      ),
                  })
                )
                .min(1),
            otherwise: (schema) =>
              schema.of(
                Yup.object().shape({
                  type: yupString.optional(),
                  phoneNumber: Yup.mixed().test(
                    'isPhone',
                    i18n.t('general.validations.invalidPhoneNumber'),
                    (phone?: PhoneNumber | Nullish) => {
                      if (isNilOrEmpty(phone?.number)) {
                        return true;
                      }
                      return isPhoneNumberValid(phone);
                    }
                  ),
                })
              ),
          }),
          emails: Yup.array().when('phoneNumbers', {
            is: (phoneNumbers?: PhoneNumberData[]) =>
              phoneNumbers?.length &&
              phoneNumbers?.length <= 1 &&
              isNilOrEmpty(phoneNumbers?.[0]?.phoneNumber?.number),
            then: (schema) =>
              schema
                .of(
                  Yup.object().shape({
                    type: yupString.optional(),
                    email: yupCustomerEmail.required(i18n.t('general.validations.phoneOrEmail')),
                  })
                )
                .min(1),
            otherwise: (schema) =>
              schema.of(
                Yup.object().shape({
                  type: yupString.optional(),
                  email: yupCustomerEmail.optional().default(''),
                })
              ),
          }),
        },
        [['emails', 'phoneNumbers']]
      ),
      selfEmployedBusinessInfoData: Yup.object().when('selfEmployed', {
        is: true,
        then: (schema) =>
          schema.shape(
            {
              countryOfRegistrationCode: yupString.required(),
              registrationNumber: yupString.when('vatNumber', {
                is: (value: string | Nullish) => isNilOrEmpty(value),
                then: yupString.required(i18n.t('general.errors.mixed.vatIdOrBusinessIdRequired')),
              }),
              vatNumber: yupString.when('registrationNumber', {
                is: (value: string | Nullish) => isNilOrEmpty(value),
                then: yupString.required(i18n.t('general.errors.mixed.vatIdOrBusinessIdRequired')),
              }),
              tradeName: yupString.required(),
              fileNumber: yupString.optional().default(null),
            },
            [['registrationNumber', 'vatNumber']]
          ),
        otherwise: (schema) => schema.nullable(),
      }),
      addressData: Yup.object().when('addressData', {
        is: (addressData: AddressRequestBodyV2 | Nullish) =>
          ifElse(
            isNotNilOrEmpty,
            pipe<
              [CommonAddressRequestBody | Nullish],
              CommonAddressRequestBody | object,
              (string | {latitude: string; longitude: string} | boolean | Nullish)[],
              boolean
            >(defaultTo({}), values, any(isNotNilOrEmpty)),
            always(false)
          )(addressData?.address),
        then: (schema) =>
          schema.shape({
            address: Yup.object().shape({
              street: Yup.string().required().nullable(),
              descriptiveNumber: Yup.string().optional().nullable(),
              orientationNumber: Yup.string().optional().nullable(),
              city: Yup.string().required().nullable(),
              district: Yup.string().optional().nullable(),
              zip: Yup.string().required().nullable(),
              country: Yup.string().required().nullable(),
              addressComplement: Yup.string().nullable().optional().nullable(),
              coordinates: Yup.object()
                .shape({
                  latitude: yupString,
                  longitude: yupString,
                })
                .optional()
                .nullable(),
              postal: Yup.string().optional().nullable(),
            }),
            type: Yup.string().nullable().optional(),
          }),
      }),
    },
    [['addressData', 'addressData']]
  );
